import { runInAction } from 'mobx';
import Action from '../../../decorators/Action';
import Computed from '../../../decorators/Computed';
import Observable from '../../../decorators/Observable';
import StoreFieldInSession from '../../../decorators/StoreFieldInSession';
import AbstractSessionModel from '../../../models/AbstractSessionModel';
import { IWizardDataModel } from '../../../models/IWizardDataModel';
import Api from '../../../services/Api';
import WizardSessionData from '../../../services/WizardSessionData';
import ActivityEntity from '../domain/ActivityEntity';
import AreaEntity from '../domain/AreaEntity';
import MainCategoryEntity from '../domain/MainCategoryEntity';
import BusinessModel from '../../FindYourBusiness/models/BusinessModel';
import { STEP_SITE_BUILDER } from '../../steps-definitions';
import { WIZARD_STEPS } from '../../wizard-steps-description';
import Step from '../../SiteBuilder/Step';
import StepperModel from '../../../models/StepperModel';

interface CategoryData {
  subcategories: Subcategory[];
  related: RelatedCategory[];
  main: MainCategory | null;
}

interface Subcategory {
  name: string;
  isCustom: boolean;
  keywords: string[];
}

interface RelatedCategory {
  name: string;
  keywords: string[];
  subcategories: RelatedSubcategory[];
}

interface RelatedSubcategory {
  name: string;
  keywords: string[];
}

interface MainCategory {
  name: string;
  keywords: string[];
}

export const DYNAMIC_STEP_AREAS = 'dynamicStepAreas';
export const DYNAMIC_STEP_ACTIVITIES = 'dynamicStepActivities';
export const DYNAMIC_STEP_KEYWORDS = 'dynamicStepKeywords';

const STEP_DEFINITION = {
  MainCategory: {
    getNextStep: async (
      areas: AreaEntity[],
      activities: ActivityEntity[],
      mainCategoryData: MainCategoryEntity
    ): Promise<{ step: string; data: any }> => {
      if (areas.length === 0 && mainCategoryData) {
        return {
          step: 'KeywordsOfTheMainCategory',
          data: {
            keywordEntity: mainCategoryData
          }
        };
      }

      if (areas.length > 0) {
        return {
          step: 'Areas',
          data: {}
        };
      }

      return {
        step: '__FINAL__',
        data: {}
      };
    }
  },
  KeywordsOfTheMainCategory: {
    getNextStep: async (
      areas: AreaEntity[],
      activities: ActivityEntity[],
      mainCategoryData: MainCategoryEntity
    ): Promise<{ step: string; data: any }> => {
      if (activities.length === 0) {
        return { step: '__FINAL__', data: {} };
      }

      if (activities.length > 0) {
        return {
          step: 'Activities',
          data: {}
        };
      }
    }
  },
  Areas: {
    getNextStep: async (
      areas: AreaEntity[],
      activities: ActivityEntity[],
      mainCategoryData: MainCategoryEntity
    ): Promise<{ step: string; data: any }> => {
      //[1] first we need to show all the keywords for non custom areas
      let nonCustomCheckedAreas = areas.filter((area) => {
        return !area.isCustomArea && area.isChecked;
      });

      if (nonCustomCheckedAreas.length > 0) {
        // find the first are with areKeywordsProvided = false
        const area = nonCustomCheckedAreas.find((area) => {
          return !area.areKeywordsProvided;
        });

        if (area) {
          return {
            step: 'AreaKeywords',
            data: {
              keywordEntity: area
            }
          };
        }
      }

      //[2] then we show the keywords for custom areas
      let isThereAnyCustomArea = areas.some((area) => {
        return area.isCustomArea;
      });

      if (isThereAnyCustomArea) {
        const allCustomAreas = areas.filter((area) => {
          return area.isCustomArea;
        });

        // find the first area with areAreaKeywordsProvided = false
        const area = allCustomAreas.find((area) => {
          return !area.areKeywordsProvided;
        });

        if (area) {
          return {
            step: 'AreaKeywords',
            data: {
              keywordEntity: area
            }
          };
        }
      }

      if (activities.length === 0) {
        return { step: '__FINAL__', data: {} };
      }

      return {
        step: 'Activities',
        data: {}
      };
    }
  },
  Activities: {
    getNextStep: async (
      areas: AreaEntity[],
      activities: ActivityEntity[],
      mainCategoryData: MainCategoryEntity
    ): Promise<{ step: string; data: any }> => {
      const activitiesWithAreas = activities.filter((activity) => {
        return activity.hasAreas && activity.isChecked;
      });

      const activtiesWithoutAreas = activities.filter((activity) => {
        return !activity.hasAreas && activity.isChecked;
      });

      if (
        activitiesWithAreas.length === 0 &&
        activtiesWithoutAreas.length === 0
      ) {
        return {
          step: '__FINAL__',
          data: {}
        };
      }

      if (
        activitiesWithAreas.length === 0 &&
        activtiesWithoutAreas.length > 0
      ) {
        const firstActivityWithNoKeywordsProvided = activtiesWithoutAreas.find(
          (activity) => {
            return activity.areKeywordsProvided === false;
          }
        );

        if (firstActivityWithNoKeywordsProvided) {
          return {
            step: 'ActivityWithoutAreasKeywords',
            data: {
              keywordEntity: firstActivityWithNoKeywordsProvided
            }
          };
        }
      }

      const firstActivityWithAreasAndNoKeywordsProvided =
        activitiesWithAreas.find((activity) => {
          return !activity.areAreasProvided;
        });

      if (firstActivityWithAreasAndNoKeywordsProvided) {
        // here we show step with areas of the selected activity
        return {
          step: 'ActivityAreas',
          data: {
            activityWithAreas: firstActivityWithAreasAndNoKeywordsProvided
          }
        };
      }
    }
  },
  ActivityWithoutAreasKeywords: {
    getNextStep: async (
      areas: AreaEntity[],
      activities: ActivityEntity[],
      mainCategoryData: MainCategoryEntity
    ): Promise<{ step: string; data: any }> => {
      const activtiesWithoutAreas = activities.filter((activity) => {
        return !activity.hasAreas && activity.isChecked;
      });

      const firstActivityWithNoKeywordsProvided = activtiesWithoutAreas.find(
        (activity) => {
          return !activity.areKeywordsProvided;
        }
      );

      if (firstActivityWithNoKeywordsProvided) {
        return {
          step: 'ActivityWithoutAreasKeywords',
          data: {
            keywordEntity: firstActivityWithNoKeywordsProvided
          }
        };
      }

      return { step: '__FINAL__', data: {} };
    }
  },
  ////////////////////////////////////////////////
  // In this step we show the areas (subcategories) of the selected activities (related)
  // and we ask the user to provide keywords for them
  ////////////////////////////////////////////////
  ActivityAreas: {
    getNextStep: async (
      areas: AreaEntity[],
      activities: ActivityEntity[],
      mainCategoryData: MainCategoryEntity
    ): Promise<{ step: string; data: any }> => {
      // first we need to find ALL OTHERS selected areas for activities
      // and show them to the user, so the user can select them
      const activitiesWithAreas = activities.filter((activity) => {
        return activity.hasAreas && activity.isChecked;
      });

      // find the next activity with areas to show
      const nextActivityToShow = activitiesWithAreas.find((activity) => {
        return !activity.areAreasProvided;
      });

      if (nextActivityToShow) {
        // here we show step with areas of the selected activity
        return {
          step: 'ActivityAreas',
          data: {
            activityWithAreas: nextActivityToShow
          }
        };
      }

      // HERE all the areas for the selected activities are shown.
      // Then we need to add keywords for the areas.

      // get the first activity with areas and no keywords provided for that areas
      const firstActivityForKeywrords = activitiesWithAreas.find((activity) => {
        return activity.firstAreaWithNoKeywordsProvided;
      });

      if (firstActivityForKeywrords) {
        const firstAreaWithNoKeywordsProvided =
          firstActivityForKeywrords.firstAreaWithNoKeywordsProvided;

        return {
          step: 'KeywordsActivityAreas',
          data: {
            activity: firstActivityForKeywrords,
            firstAreaWithNoKeywordsProvided
          }
        };
      }

      const activtiesWithoutAreas = activities.filter((activity) => {
        return !activity.hasAreas && activity.isChecked;
      });

      const firstNoAreasActivityWithNoKeywordsProvided =
        activtiesWithoutAreas.find((activity) => {
          return !activity.areKeywordsProvided;
        });

      if (firstNoAreasActivityWithNoKeywordsProvided) {
        return {
          step: 'ActivityWithoutAreasKeywords',
          data: {
            keywordEntity: firstNoAreasActivityWithNoKeywordsProvided
          }
        };
      }

      return {
        step: '',
        data: {}
      };
    }
  },
  KeywordsActivityAreas: {
    getNextStep: async (
      areas: AreaEntity[],
      activities: ActivityEntity[],
      mainCategoryData: MainCategoryEntity
    ): Promise<{ step: string; data: any }> => {
      const activitiesWithAreas = activities.filter((activity) => {
        return activity.hasAreas && activity.isChecked;
      });

      if (activitiesWithAreas.length > 0) {
        const firstActivityWithNoKeywordsProvided = activitiesWithAreas.find(
          (activity) => {
            return activity.firstAreaWithNoKeywordsProvided;
          }
        );

        if (firstActivityWithNoKeywordsProvided) {
          const firstAreaWithNoKeywordsProvided =
            firstActivityWithNoKeywordsProvided.firstAreaWithNoKeywordsProvided;

          return {
            step: 'KeywordsActivityAreas',
            data: {
              activity: firstActivityWithNoKeywordsProvided,
              firstAreaWithNoKeywordsProvided
            }
          };
        }
      }

      // show the keywords of the activity with no areas
      const activtiesWithoutAreas = activities.filter((activity) => {
        return !activity.hasAreas && activity.isChecked;
      });

      const firstActivityWithNoKeywordsProvided = activtiesWithoutAreas.find(
        (activity) => {
          return !activity.areKeywordsProvided;
        }
      );

      if (firstActivityWithNoKeywordsProvided) {
        return {
          step: 'ActivityWithoutAreasKeywords',
          data: {
            keywordEntity: firstActivityWithNoKeywordsProvided
          }
        };
      }

      return { step: '__FINAL__', data: {} };
    }
  },
  AreaKeywords: {
    getNextStep: async (
      areas: AreaEntity[],
      activities: ActivityEntity[],
      mainCategoryData: MainCategoryEntity
    ): Promise<{ step: string; data: any }> => {
      // THE CASE WHEN THE USER ADDED NON CUSTOM AREAS
      let nonCustomCheckedAreas = areas.filter((area) => {
        return !area.isCustomArea && area.isChecked;
      });

      if (nonCustomCheckedAreas.length > 0) {
        // find the first are with areKeywordsProvided = false
        const area = nonCustomCheckedAreas.find((area) => {
          return !area.areKeywordsProvided;
        });

        if (area) {
          return {
            step: 'AreaKeywords',
            data: {
              keywordEntity: area
            }
          };
        }
      }

      // THE CASE WHEN THE USER HAS ADDED A CUSTOM AREA
      let isThereAnyCustomArea = areas.some((area) => {
        return area.isCustomArea;
      });

      if (isThereAnyCustomArea) {
        const allCustomAreas = areas.filter((area) => {
          return area.isCustomArea;
        });

        // find the first area with areAreaKeywordsProvided = false
        const area = allCustomAreas.find((area) => {
          return !area.areKeywordsProvided;
        });

        if (area) {
          return {
            step: 'AreaKeywords',
            data: {
              keywordEntity: area
            }
          };
        }
      }

      if (activities.length === 0) {
        return { step: '__FINAL__', data: {} };
      }

      return {
        step: 'Activities',
        data: {}
      };
    }
  }
};

class CategoryKeywordModel
  extends AbstractSessionModel
  implements IWizardDataModel {
  @StoreFieldInSession({
    serialize: (value: AreaEntity[]) => {
      return value.map((item) => item.toJSON());
    },
    deserialize: (value: any[]) =>
      value.map((item) => {
        return AreaEntity.fromJSON(item);
      })
  })
  @Observable()
  public areas: AreaEntity[] = [];

  @StoreFieldInSession({
    serialize: (value: ActivityEntity[]) => value.map((item) => item.toJSON()),
    deserialize: (value: any[]) =>
      value.map((item) => {
        return ActivityEntity.fromJSON(item);
      })
  })
  @Observable()
  public activities: ActivityEntity[] = [];

  @StoreFieldInSession({
    serialize: (value: MainCategoryEntity) => (value ? value.toJSON() : null),
    deserialize: (value: any) => {
      return MainCategoryEntity.fromJSON(value);
    }
  })
  @Observable()
  public mainCategoryData: MainCategoryEntity = null;

  @StoreFieldInSession()
  @Observable()
  public currentStep = 'MainCategory';

  @StoreFieldInSession({
    serialize: (value: any[]) => {
      let serializedValue = [];

      // for all keys in first level of the array, check if key value has method toJSON
      value.forEach((item) => {
        let serializedObject = {
          step: item.step,
          data: { ...item.data }
        };

        if (
          item &&
          Object.prototype.toString.call(serializedObject.data) ===
          '[object Object]'
        ) {
          for (const key in serializedObject.data) {
            if (
              Object.prototype.hasOwnProperty.call(serializedObject.data, key)
            ) {
              const dataProperty = serializedObject.data[key];
              if (
                dataProperty &&
                Object.prototype.toString.call(dataProperty) ===
                '[object Object]' &&
                typeof dataProperty.toJSON === 'function'
              ) {
                serializedObject.data[key] = dataProperty.toJSON();
              }
            }
          }
        }

        serializedValue.push(serializedObject);
      });

      return serializedValue;
    },
    deserialize: (value: any[]) => {
      try {
        const deserialized = value.map((stepItem: any) => {
          if (
            stepItem &&
            Object.prototype.toString.call(stepItem.data) === '[object Object]'
          ) {
            for (const key in stepItem.data) {
              if (Object.prototype.hasOwnProperty.call(stepItem.data, key)) {
                const dataProperty = stepItem.data[key];
                if (
                  dataProperty &&
                  Object.prototype.toString.call(dataProperty) ===
                  '[object Object]' &&
                  dataProperty.type
                ) {
                  if (dataProperty.type === 'AreaEntity' && dataProperty.data) {
                    stepItem.data[key] = AreaEntity.fromJSON(dataProperty);
                  } else if (
                    dataProperty.type === 'ActivityEntity' &&
                    dataProperty.data
                  ) {
                    stepItem.data[key] = ActivityEntity.fromJSON(dataProperty);
                  } else if (
                    dataProperty.type === 'MainCategoryEntity' &&
                    dataProperty.data
                  ) {
                    stepItem.data[key] =
                      MainCategoryEntity.fromJSON(dataProperty);
                  }
                }
              }
            }
          }
          return stepItem;
        });

        return deserialized;
      } catch (e) {
        return []; // Return empty array in case of parsing error or if value is not a valid JSON
      }
    }
  })
  @Observable()
  public dynamicSteps = [];

  constructor() {
    super();
    super.initFields(); // Initialize fields from session storage
  }

  @Action()
  async fetchCategories(categorName: string) {
    try {
      let result = await Api.findSubcategoryInfo(categorName);

      result.data.subcategories.forEach((area) => {
        this.areas.push(new AreaEntity(area));
      });

      result.data.related.forEach((activity) => {
        this.activities.push(new ActivityEntity(activity));
      });

      if (result.data.main) {
        this.mainCategoryData = new MainCategoryEntity(result.data.main);
      }

      return result;
    } catch (error) {
      throw error;
    }
  }

  @Action()
  async moveToNextStep() {
    const currentStep = STEP_DEFINITION[this.currentStep];

    const { step, data } = await currentStep.getNextStep(
      this.areas,
      this.activities,
      this.mainCategoryData
    );

    if (step === '__FINAL__') {
      await StepperModel.firstNextStepFromPossibleNextSteps();

      return;
    }

    runInAction(() => {
      console.log('Setting current step to: ', step);
      this.currentStep = step;
      this.dynamicSteps.push({
        step: step,
        data: data
      });
    });
  }

  @Action()
  removeAllCustomAreas() {
    this.areas = this.areas.filter((area) => {
      return !area.isCustomArea;
    });
  }

  @Action()
  removeAllCustomActivities() {
    this.activities = this.activities.filter((area) => {
      return !area.isCustomActivity;
    });
  }

  @Action()
  addNewArea(area: AreaEntity) {
    this.areas.push(area);
  }

  @Action()
  addNewActivity(activity: ActivityEntity) {
    this.activities.push(activity);
  }

  get isMaxActivitySelected() {
    const allChecked = this.activities.filter((activity) => activity.isChecked);
    return allChecked.length >= 3;
  }


  @Action()
  async fetchKeywordsForActivitiyCustomAreas() {
    let customAreasForActivities = this.activities.filter((activity) => {
      return activity.customAreas.length > 0;
    });

    const category = BusinessModel.businessWizardData.getWizardData().category;

    let allCustomAreasForActivities = customAreasForActivities
      .map((activity) => {
        return activity.customAreas.map((area) => {
          return area;
        });
      })
      .flat();

    await Promise.all(
      allCustomAreasForActivities.map(async (area) => {
        let keywordsResult = await Api.getKeywordsForCustomArea(
          area.name,
          category
        );

        if (keywordsResult.status !== 'success') {
          return {
            name: area.name,
            keywords: []
          };
        }

        const keywords = keywordsResult.data.keywords || [];
        area.keywords = keywords;
      })
    );
  }

  @Action()
  async fetchKeywordsForCustomAreas() {
    let customAreas = this.areas.filter((area) => {
      return area.isCustomArea;
    });

    const category = BusinessModel.businessWizardData.getWizardData().category;

    let customAreasWithKeywords = await Promise.all(
      customAreas.map(async (area) => {
        let keywordsResult = await Api.getKeywordsForCustomArea(
          area.name,
          category
        );

        if (keywordsResult.status !== 'success') {
          return {
            name: area.name,
            keywords: []
          };
        }

        const keywords = keywordsResult.data.keywords || [];
        return {
          name: area.name,
          keywords: keywords
        };
      })
    );

    customAreasWithKeywords.forEach((area) => {
      const existingArea = this.areas.find(
        (a) => a.isCustomArea && a.name === area.name
      );

      if (existingArea) {
        existingArea.setKeywords(area.keywords);
      }
    });
  }

  @Computed()
  get getCurrentStepData(): {
    step: string;
    data: any;
  } {
    return this.dynamicSteps[this.dynamicSteps.length - 1];
  }

  /**
   * This method is used to get the data for the areas and activities steps
   * and send it to the api
   * @returns
   */
  getOutputCategoryData(): CategoryData {
    const subcategories = this.areas
      .filter((area) => {
        return area.isChecked;
      })
      .map((area) => {
        return {
          name: area.name,
          isCustom: area.isCustomArea,
          keywords: area.getAllAddedKeywords()
        };
      });

    const related = this.activities
      .filter((related) => {
        return related.isChecked;
      })
      .map((activity) => {
        return {
          name: activity.name,
          keywords: activity.getAllAddedKeywords(),
          subcategories: activity.areas
            .filter((area) => {
              return area.checked;
            })
            .map((area) => {
              return {
                name: area.name,
                keywords: [...area.checkedKeywords, ...area.customAddedKeywords]
              };
            })
        };
      });

    const main = this.mainCategoryData
      ? {
        name: this.mainCategoryData.name,
        keywords: this.mainCategoryData.getAllAddedKeywords()
      }
      : null;

    return {
      subcategories,
      related,
      main
    };
  }

  getWizardData(): any {
    return {
      subcategories: this.areas
        .filter((area) => area.isChecked)
        .map((area) => {
          return {
            name: area.name
          };
        }),
      related_categories: this.activities
        .filter((activity) => activity.isChecked)
        .map((activity) => {
          return {
            name: activity.name
          };
        })
    };
  }

  getSessionKey(): string {
    return 'CategoryKeywordModel';
  }

  @Action()
  removeCustomAreaByName(name: string) {
    this.areas = this.areas.filter(
      (area) => !(area.isCustomArea && area.displayName.toLowerCase() === name)
    );
  }

  @Action()
  removeCustomActivityByName(name: string) {
    this.activities = this.activities.filter(
      (activity) => !(activity.isCustomActivity && activity.displayName.toLowerCase() === name)
    );
  }
}

const model = new CategoryKeywordModel();
WizardSessionData.addModel(model); // Register model with session data manager
export default model;
